'use client';

import { useEffect, useRef, useState } from 'react';
import { SAFE_GLYPHS, randomGlyph } from '@/lib/effects/decode';
import { prefersReducedMotion } from '@/lib/utils';

const CHAR_STAGGER_MS = 32;
const FADE_MS = 220;
/** When the return wave starts after the out wave begins — overlapping so the
    ripple reads as one continuous pass, not two separate fades. */
const RIPPLE_RETURN_MS = 260;
/** How often the glyphs re-scramble while the wave is out. */
const SCRAMBLE_TICK_MS = 65;
/** Characters dim to this while scrambling — low enough to soften the glyph
    swaps, high enough that the "code" flicker stays clearly visible. */
const DIM_OPACITY = 0.35;

interface CharFadeLabelProps {
  text: string;
  /** ms before the mount ripple runs — stagger siblings with this. */
  mountDelay?: number;
  className?: string;
}

/**
 * Nav-label ripple: per-character fade AND terminal scramble in one pass.
 * A soft opacity/blur wave moves left-to-right through the characters; while
 * a character is dimmed it visibly cycles through glyphs (alphanumeric only —
 * safe for display fonts like Six Caps whose subsets lack punctuation), then
 * the wave returns and it resolves to the real character.
 *
 * Runs on mount (staggered via `mountDelay`) and on hover of the enclosing
 * link. The accessible name is a visually-hidden copy of the text (never
 * aria-label on a generic span); the animated characters are aria-hidden.
 *
 * Performance: glyph swaps write textContent imperatively — React re-renders
 * only on the two wave flips per ripple, not per scramble tick. The ripple is
 * skipped entirely while the label is display:none (e.g. the desktop nav on
 * mobile), and no timers exist outside the ~700ms a ripple is live. The
 * container width is locked (fractional, via getBoundingClientRect) during a
 * ripple so sibling links never shift. Skipped under prefers-reduced-motion.
 */
export function CharFadeLabel({ text, mountDelay = 0, className }: CharFadeLabelProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [out, setOut] = useState(false);
  const rippling = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;

    let outTimer: ReturnType<typeof setTimeout> | undefined;
    let doneTimer: ReturnType<typeof setTimeout> | undefined;
    let interval: ReturnType<typeof setInterval> | undefined;

    const chars = () => el.querySelectorAll<HTMLElement>('[data-char]');

    const restore = () => {
      chars().forEach((span, i) => {
        span.textContent = text[i] ?? '';
      });
    };

    const scramble = () => {
      chars().forEach((span, i) => {
        const ch = text[i] ?? '';
        span.textContent = /\s/.test(ch) ? ch : randomGlyph(Math.random, SAFE_GLYPHS);
      });
    };

    const ripple = () => {
      if (rippling.current) return;
      const width = el.getBoundingClientRect().width;
      if (width === 0) return; // display:none (e.g. desktop nav on mobile) — skip
      rippling.current = true;
      el.style.width = `${width}px`;
      setOut(true);
      scramble();
      interval = setInterval(scramble, SCRAMBLE_TICK_MS);
      outTimer = setTimeout(() => {
        clearInterval(interval);
        restore();
        setOut(false);
      }, RIPPLE_RETURN_MS);
      doneTimer = setTimeout(() => {
        el.style.width = '';
        rippling.current = false;
      }, RIPPLE_RETURN_MS + FADE_MS + text.length * CHAR_STAGGER_MS);
    };

    const mountTimer = setTimeout(ripple, mountDelay);

    const anchor = el.closest('a') ?? el;
    anchor.addEventListener('mouseenter', ripple);

    return () => {
      clearTimeout(mountTimer);
      clearTimeout(outTimer);
      clearTimeout(doneTimer);
      if (interval) clearInterval(interval);
      anchor.removeEventListener('mouseenter', ripple);
      restore();
      el.style.width = '';
      rippling.current = false;
    };
  }, [text, mountDelay]);

  return (
    <span ref={ref} className={className} style={{ display: 'inline-block', whiteSpace: 'pre' }}>
      {/* Accessible name — real text, visually hidden */}
      <span className="sr-only">{text}</span>
      {text.split('').map((char, i) => (
        <span
          key={i}
          data-char
          aria-hidden="true"
          style={{
            display: 'inline-block',
            opacity: out ? DIM_OPACITY : 1,
            filter: out ? 'blur(1.5px)' : 'blur(0)',
            transition: `opacity ${FADE_MS}ms ease, filter ${FADE_MS}ms ease`,
            transitionDelay: `${i * CHAR_STAGGER_MS}ms`,
          }}
        >
          {char}
        </span>
      ))}
    </span>
  );
}
